import { createSlice } from '@reduxjs/toolkit'
import { initialState } from '../initialState/user'
import {
  userLogin,
  userLogout,
  userRegister,
  forgotPassword,
  verifyCode,
  confirmResetPassword,
  fetchUserData,
  setup2FA,
  updateProfileData,
  verifyEmailRegistration,
} from '../asyncThunks/user'
import { setAccessToken, setAuthTokens } from '@/utils/authUtils'

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    updateUser(state, action) {
      state.user = action.payload
    },
    logout(state) {
      state.user = null
      state.isAuthenticated = false
    },
    resetError(state) {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(verifyEmailRegistration.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(verifyEmailRegistration.fulfilled, (state) => {
        state.loading = false
        state.error = null
        state.successMessage = 'Email verified successfully'
      })
      .addCase(verifyEmailRegistration.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
      .addCase(userLogin.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(userLogin.fulfilled, (state, action) => {
        state.loading = false
        state.user = action.payload.user
        state.isAuthenticated = true
        state.error = null
        state.token = action.payload.access
        setAuthTokens(action.payload.access, action.payload.refresh)
      })
      .addCase(userLogin.rejected, (state, action) => {
        state.loading = false
        state.error = JSON.parse(action.payload as string)
      })
      .addCase(fetchUserData.pending, (state) => {
        state.loading = true
        state.userLoading = true
        state.error = null
      })
      .addCase(fetchUserData.fulfilled, (state, action) => {
        state.loading = false
        state.user = action.payload
        state.isAuthenticated = true
        state.userLoading = false
        state.error = null
      })
      .addCase(fetchUserData.rejected, (state, action) => {
        state.loading = false
        state.userLoading = false
        state.error = action.payload as string
      })
      .addCase(userLogout.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(userLogout.fulfilled, (state) => {
        state.user = null
        state.isAuthenticated = false
        state.token = ''
        state.temp_token = ''
        state.loading = false
        state.error = null
      })
      .addCase(userLogout.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
      .addCase(userRegister.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(userRegister.fulfilled, (state, action) => {
        state.loading = false
        state.user = action.payload.user
        state.isAuthenticated = true
        state.error = null
      })
      .addCase(userRegister.rejected, (state, action) => {
        state.loading = false
        state.error = JSON.parse(action.payload as string)
      })
      .addCase(forgotPassword.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(forgotPassword.fulfilled, (state) => {
        state.loading = false
        state.error = null
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
      .addCase(verifyCode.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(verifyCode.fulfilled, (state, action) => {
        state.loading = false
        state.token = action.payload.token
        setAccessToken(state.token)
        state.error = null
      })
      .addCase(verifyCode.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
      .addCase(confirmResetPassword.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(confirmResetPassword.fulfilled, (state, action) => {
        state.loading = false
        state.error = null
        // If this is a first-time setup, store tokens and user info for automatic login
        if (action.payload.is_first_time_setup && action.payload.access && action.payload.user) {
          state.user = action.payload.user
          state.isAuthenticated = true
          state.token = action.payload.access
          setAuthTokens(action.payload.access, action.payload.refresh)
        }
      })
      .addCase(confirmResetPassword.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
      .addCase(setup2FA.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(setup2FA.fulfilled, (state) => {
        state.loading = false
      })
      .addCase(setup2FA.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
      .addCase(updateProfileData.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateProfileData.fulfilled, (state, action) => {
        state.loading = false
        state.user = {
          ...state.user!,
          profile_picture: action.payload.profile_picture,
          name: action.payload.name,
        }
        state.error = null
      })
      .addCase(updateProfileData.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
  },
})

export const { updateUser, logout, resetError } = userSlice.actions

export default userSlice.reducer
